import { API } from "api";
import { convertToUtc, getTimeZone, ToFormData } from "hooks";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const InfinityScroll = ({
  idParam,
  records,
  setRecords,
  endpointKey,
  setResult,
  enableSelect = false,
  renderHeader,
  renderContent,
  ...other
}) => {
  const { t } = useTranslation();
  const rowsPerPage = 20;
  const maxSelect = enableSelect ? 100 : 0;

  const [loading, setLoading] = useState(false);
  const [pageNum, setPageNum] = useState(-1); // -1 until first fetch
  const [totalItems, setTotalItems] = useState(null);

  const containerRef = useRef(null);
  const loaderRef = useRef(null);

  // Guards to prevent double fetches (StrictMode, rapid intersections, etc.)
  const inFlight = useRef(false);
  const fetchedPages = useRef(new Set());

  const baseParam = useMemo(
    () => ({
      id: idParam ?? "",
      rowsPerPage,
      searchVal: "",
      fromDate: "",
      toDate: "",
      timeZone: getTimeZone(),
    }),
    [idParam, rowsPerPage]
  );

  const fetchPage = (page) => {
    if (inFlight.current) return;
    if (fetchedPages.current.has(page)) return; // don't refetch same page

    inFlight.current = true;
    setLoading(true);

    const payload = ToFormData({
      ...baseParam,
      pageNum: page,
      today: convertToUtc(new Date()),
      fromDate: convertToUtc(baseParam.fromDate),
      toDate: convertToUtc(baseParam.toDate),
      maxSelect,
    });

    API(endpointKey).post(payload, (res) => {
      if (res && res.status === 200) {
        setRecords((prev) =>
          page === 0 ? [...res.result] : [...prev, ...res.result]
        );
        setTotalItems(res.totalRecord || 0);
        setResult && setResult({ ...res });
        fetchedPages.current.add(page);
        setPageNum(page);
      }
      inFlight.current = false;
      setLoading(false);
    });
  };

  // Reset and load first page when idParam (or endpoint/rowsPerPage) changes
  useEffect(() => {
    setRecords([]);
    setTotalItems(null);
    fetchedPages.current.clear();
    setPageNum(-1);
    fetchPage(0); // initial load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idParam, endpointKey, rowsPerPage]);

  // IntersectionObserver to load subsequent pages
  useEffect(() => {
    const rootEl = containerRef.current || null;
    const node = loaderRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry.isIntersecting) return;
        if (loading || inFlight.current) return;
        if (totalItems !== null && records.length >= totalItems) return;

        const nextPage = pageNum + 1;
        fetchPage(nextPage);
      },
      {
        root: rootEl, // observe inside the scrollable container
        threshold: 0, // trigger as soon as it appears
        rootMargin: "200px", // prefetch a bit earlier
      }
    );

    observer.observe(node);
    return () => {
      observer.unobserve(node);
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, totalItems, records.length, pageNum]);

  return (
    <div {...other}>
      {renderHeader && renderHeader()}
      <div
        ref={containerRef}
        style={{ maxHeight: "calc(100vh - 130px)", overflow: "auto" }}
      >
        {records.length > 0 ? (
          renderContent(records, totalItems)
        ) : !loading ? (
          <div className="flex items-center justify-center my-[30px]">
            <span className="text-[18px] font-medium text-gray-500">
              {t("dataNotAvailable")}
            </span>
          </div>
        ) : null}

        <div ref={loaderRef}>
          {loading && (
            <div className="h-10 flex justify-center items-center">
              <span className="text-[18px] font-medium text-gray-500">
                {t("loadingDots")}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InfinityScroll;
